import { createSlice } from "@reduxjs/toolkit";

const loadKeys = [
  "adaptiveHomepage",
  "allQuestionList",
  "subjectList",
  "topicWise",
  "subTopicList",
  "learningSummary",
  "solution",
  "singleQuestion",
  "singleSave",
  "submit",
  "userAnalysis",
];

const initialLoadState = loadKeys.reduce((acc, key) => {
  acc[key] = false;
  return acc;
}, {});

const setLoading = (state, key, value) => {
  state.loading[key] = value;
};

const adaptiveQBankSlice = createSlice({
  name: "adaptiveQBank",
  initialState: {
    adaptiveHomepage: null,
    allQuestionList: null,
    subjectList: null,
    topicWise: null,
    subTopicList: null,
    learningSummary: null,
    solution: null,
    singleQuestion: null,
    singleSaveResult: null,
    submitResult: null,
    userAnalysis: null,
    loading: initialLoadState,
    error: null,
  },
  reducers: {
    clearAdaptiveQBankError: (state) => {
      state.error = null;
    },

    // 1 POST adaptive_homepage
    fetchAdaptiveHomepageRequest: (state) => {
      setLoading(state, "adaptiveHomepage", true);
      state.error = null;
    },
    fetchAdaptiveHomepageSuccess: (state, action) => {
      setLoading(state, "adaptiveHomepage", false);
      state.adaptiveHomepage = action.payload;
    },
    fetchAdaptiveHomepageFailure: (state, action) => {
      setLoading(state, "adaptiveHomepage", false);
      state.error = action.payload;
    },

    // 2 GET allquestionlist
    fetchAdaptiveAllQuestionListRequest: (state) => {
      setLoading(state, "allQuestionList", true);
      state.error = null;
    },
    fetchAdaptiveAllQuestionListSuccess: (state, action) => {
      setLoading(state, "allQuestionList", false);
      state.allQuestionList = action.payload;
    },
    fetchAdaptiveAllQuestionListFailure: (state, action) => {
      setLoading(state, "allQuestionList", false);
      state.error = action.payload;
    },

    // 3 POST adaptive_subject_list
    postAdaptiveSubjectListRequest: (state) => {
      setLoading(state, "subjectList", true);
      state.error = null;
    },
    postAdaptiveSubjectListSuccess: (state, action) => {
      setLoading(state, "subjectList", false);
      state.subjectList = action.payload;
    },
    postAdaptiveSubjectListFailure: (state, action) => {
      setLoading(state, "subjectList", false);
      state.error = action.payload;
    },

    // 4 POST adaptive_subject_topic_list
    postAdaptiveTopicWiseRequest: (state) => {
      setLoading(state, "topicWise", true);
      state.error = null;
    },
    postAdaptiveTopicWiseSuccess: (state, action) => {
      setLoading(state, "topicWise", false);
      state.topicWise = action.payload;
    },
    postAdaptiveTopicWiseFailure: (state, action) => {
      setLoading(state, "topicWise", false);
      state.error = action.payload;
    },

    // 5 POST adaptiveQBSubtopics_list
    postAdaptiveSubTopicListRequest: (state) => {
      setLoading(state, "subTopicList", true);
      state.error = null;
    },
    postAdaptiveSubTopicListSuccess: (state, action) => {
      setLoading(state, "subTopicList", false);
      state.subTopicList = action.payload;
    },
    postAdaptiveSubTopicListFailure: (state, action) => {
      setLoading(state, "subTopicList", false);
      state.error = action.payload;
    },

    // 6 POST adaptiveLearningSummary
    postAdaptiveLearningSummaryRequest: (state) => {
      setLoading(state, "learningSummary", true);
      state.error = null;
    },
    postAdaptiveLearningSummarySuccess: (state, action) => {
      setLoading(state, "learningSummary", false);
      state.learningSummary = action.payload;
    },
    postAdaptiveLearningSummaryFailure: (state, action) => {
      setLoading(state, "learningSummary", false);
      state.error = action.payload;
    },

    // 7 GET practise_solution
    fetchAdaptiveSolutionRequest: (state) => {
      setLoading(state, "solution", true);
      state.error = null;
    },
    fetchAdaptiveSolutionSuccess: (state, action) => {
      setLoading(state, "solution", false);
      state.solution = action.payload;
    },
    fetchAdaptiveSolutionFailure: (state, action) => {
      setLoading(state, "solution", false);
      state.error = action.payload;
    },

    // 8 GET exam_single_question (adaptive params)
    fetchAdaptiveSingleQuestionRequest: (state) => {
      setLoading(state, "singleQuestion", true);
      state.error = null;
    },
    fetchAdaptiveSingleQuestionSuccess: (state, action) => {
      setLoading(state, "singleQuestion", false);
      state.singleQuestion = action.payload;
    },
    fetchAdaptiveSingleQuestionFailure: (state, action) => {
      setLoading(state, "singleQuestion", false);
      state.error = action.payload;
    },

    // 9 POST exam_single_save
    postAdaptiveSingleSaveRequest: (state) => {
      setLoading(state, "singleSave", true);
      state.error = null;
    },
    postAdaptiveSingleSaveSuccess: (state, action) => {
      setLoading(state, "singleSave", false);
      state.singleSaveResult = action.payload;
    },
    postAdaptiveSingleSaveFailure: (state, action) => {
      setLoading(state, "singleSave", false);
      state.error = action.payload;
    },

    // 10 POST exam_submit
    postAdaptiveSubmitRequest: (state) => {
      setLoading(state, "submit", true);
      state.error = null;
    },
    postAdaptiveSubmitSuccess: (state, action) => {
      setLoading(state, "submit", false);
      state.submitResult = action.payload;
    },
    postAdaptiveSubmitFailure: (state, action) => {
      setLoading(state, "submit", false);
      state.error = action.payload;
    },

    // 11 POST UserAdaptiveAnalysis
    postAdaptiveUserAnalysisRequest: (state) => {
      setLoading(state, "userAnalysis", true);
      state.error = null;
      // Prevent stale previous-focus-set analysis from flashing on summary screen.
      state.userAnalysis = null;
    },
    postAdaptiveUserAnalysisSuccess: (state, action) => {
      setLoading(state, "userAnalysis", false);
      state.userAnalysis = action.payload;
    },
    postAdaptiveUserAnalysisFailure: (state, action) => {
      setLoading(state, "userAnalysis", false);
      state.error = action.payload;
    },
  },
});

export const {
  clearAdaptiveQBankError,
  fetchAdaptiveHomepageRequest,
  fetchAdaptiveHomepageSuccess,
  fetchAdaptiveHomepageFailure,
  fetchAdaptiveAllQuestionListRequest,
  fetchAdaptiveAllQuestionListSuccess,
  fetchAdaptiveAllQuestionListFailure,
  postAdaptiveSubjectListRequest,
  postAdaptiveSubjectListSuccess,
  postAdaptiveSubjectListFailure,
  postAdaptiveTopicWiseRequest,
  postAdaptiveTopicWiseSuccess,
  postAdaptiveTopicWiseFailure,
  postAdaptiveSubTopicListRequest,
  postAdaptiveSubTopicListSuccess,
  postAdaptiveSubTopicListFailure,
  postAdaptiveLearningSummaryRequest,
  postAdaptiveLearningSummarySuccess,
  postAdaptiveLearningSummaryFailure,
  fetchAdaptiveSolutionRequest,
  fetchAdaptiveSolutionSuccess,
  fetchAdaptiveSolutionFailure,
  fetchAdaptiveSingleQuestionRequest,
  fetchAdaptiveSingleQuestionSuccess,
  fetchAdaptiveSingleQuestionFailure,
  postAdaptiveSingleSaveRequest,
  postAdaptiveSingleSaveSuccess,
  postAdaptiveSingleSaveFailure,
  postAdaptiveSubmitRequest,
  postAdaptiveSubmitSuccess,
  postAdaptiveSubmitFailure,
  postAdaptiveUserAnalysisRequest,
  postAdaptiveUserAnalysisSuccess,
  postAdaptiveUserAnalysisFailure,
} = adaptiveQBankSlice.actions;

export default adaptiveQBankSlice.reducer;
